import { Button } from '@factorialco/deprecated-design-system'
import {
  DataTableFilter,
  FilterType,
  FinanceAccountFilter,
  ProjectionKind,
  useLedgerAccountsDataTable,
} from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import { Filter } from 'modules/core/components/DataTable/types'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import { ledgerAccountTypeLabel } from 'modules/finance/lib/accounting/accounts'

import Actions from './_Actions'
import { Amount } from './_Amount'
import { DisabledStatus } from './_DisabledStatus'
import { SyncStatus } from './_SyncStatus'
import { Type } from './_Type'

interface Props {
  legalEntitiesOptions: { label: string; value: number }[]
  onFilterChange: (data: Record<string, unknown>) => void
  defaultLegalEntityId?: number

  showEditAccount: (accountId: number) => void
  showDeleteAccount: (accountId: number, name?: string | null) => void
  showEnableDisableAccount: (accountId: number, disabled: boolean, name?: string | null) => void
  canManageJournalEntries: boolean
  canManageAccounts: boolean
  addAccountCTA?: () => void
  onExportClick?: () => void
}

const LedgerAccountsDataTable = ({
  legalEntitiesOptions,
  defaultLegalEntityId,
  onFilterChange,
  showEditAccount,
  showDeleteAccount,
  showEnableDisableAccount,
  canManageJournalEntries,
  canManageAccounts,
  addAccountCTA,
  onExportClick,
}: Props) => {
  const onUpdateLegalEntityFilter = (filter?: Filter<number>) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const filterValue = filter?.value as number | undefined
    // Clear filter
    if (filter === undefined || filterValue === undefined) {
      onFilterChange?.({ legalEntityId: undefined })
      return
    }
    onFilterChange?.({ legalEntityId: filterValue })
  }

  const source = useLedgerAccountsDataTable([
    {
      identifier: 'number',
      title: i18n.t('finance.chart_of_accounts.headers.number_v2'),
      kind: ProjectionKind.Path,
      path: 'number',
    },
    {
      identifier: 'name',
      title: i18n.t('finance.chart_of_accounts.headers.account'),
      kind: ProjectionKind.Path,
      path: 'name',
    },
    {
      identifier: 'type',
      title: i18n.t('finance.chart_of_accounts.headers.category'),
      kind: ProjectionKind.Custom,
      template: '{{ type }}',
      render: ({ type }) => {
        return <Type text={ledgerAccountTypeLabel(type)} />
      },
      sortBy: 'type',
    },
    {
      identifier: 'balance',
      title: i18n.t('finance.chart_of_accounts.headers.balance'),
      kind: ProjectionKind.Custom,
      render: ({ balance, currency }) => {
        return <Amount amountCents={balance} currency={currency} />
      },
      template: '{{ - }}',
    },
    {
      identifier: 'sync_status',
      title: i18n.t('finance.chart_of_accounts.headers.erp_id'),
      kind: ProjectionKind.Custom,
      template: '{{ sync_status }}',
      render: (account) => {
        return <SyncStatus externalId={account.externalId} />
      },
      tooltip: i18n.t('finance.chart_of_accounts.sync_status.tooltip_v3'),
    },
    {
      identifier: 'disabled',
      title: i18n.t('finance.chart_of_accounts.headers.status'),
      kind: ProjectionKind.Custom,
      template: '{{ disabled }}',
      render: ({ disabled }: { disabled: boolean }) => {
        return <DisabledStatus disabled={disabled} />
      },
    },
    {
      identifier: 'actions',
      kind: ProjectionKind.Decorative,
      width: 50,
      render: ({ id, disabled, name }: { id: number; disabled: boolean; name?: string | null }) => {
        return (
          <Actions
            ledgerAccountId={id}
            disabled={!!disabled}
            onShowEditClick={() => showEditAccount(id)}
            onShowDeleteClick={() => showDeleteAccount(id, name)}
            onShowEnableDisableClick={() => showEnableDisableAccount(id, disabled, name)}
            canManageJournalEntries={canManageJournalEntries}
          />
        )
      },
    },
  ])

  const filters: DataTableFilter<FinanceAccountFilter>[] = [
    {
      identifier: 'legal_entity_id',
      title: i18n.t('finance.workspace.legal_entity_scope.selector_label'),
      path: 'legalEntityId',
      default: !!defaultLegalEntityId,
      type: FilterType.Integer,
      onUpdateValue: onUpdateLegalEntityFilter,
      categories: [
        {
          kind: 'SingleChoice',
          operation: 'in',
          value: defaultLegalEntityId,
          options: legalEntitiesOptions,
        },
      ],
    },
    {
      identifier: 'type',
      title: i18n.t('finance.chart_of_accounts.headers.category'),
      path: 'type',
      type: FilterType.String,
      default: true,
      categories: [
        {
          kind: 'MultipleChoice',
          operation: 'in',
          options: [
            {
              label: ledgerAccountTypeLabel('bank'),
              value: 'bank',
            },
            {
              label: ledgerAccountTypeLabel('current_asset'),
              value: 'current_asset',
            },
            {
              label: ledgerAccountTypeLabel('current_liability'),
              value: 'current_liability',
            },
            {
              label: ledgerAccountTypeLabel('equity'),
              value: 'equity',
            },
            {
              label: ledgerAccountTypeLabel('expense'),
              value: 'expense',
            },
            {
              label: ledgerAccountTypeLabel('income'),
              value: 'income',
            },
            {
              label: ledgerAccountTypeLabel('non_current_asset'),
              value: 'non_current_asset',
            },
            {
              label: ledgerAccountTypeLabel('non_current_liability'),
              value: 'non_current_liability',
            },
          ],
        },
      ],
    },
  ]

  return (
    <DataTable
      disableExport
      source={source}
      pagination={{ kind: 'offset', itemsPerPage: 25 }}
      sort={{ path: 'number', direction: 'asc' }}
      filters={filters}
      customPopoverOptions={
        canManageAccounts && canManageJournalEntries ? (
          <PopoverMenu.TouchableOption
            key="finance.chart_of_accounts.export_accounts"
            onClick={onExportClick}
            title={i18n.t('common.export')}
          />
        ) : undefined
      }
      actions={
        addAccountCTA ? (
          <Button iconName="add" onClick={addAccountCTA} type="primary">
            {i18n.t('finance.chart_of_accounts.add_new_accounts')}
          </Button>
        ) : undefined
      }
    />
  )
}

export default LedgerAccountsDataTable
